import React from 'react';
import { cn } from '../../../lib/utils';

interface LogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'white' | 'minimal';
  showText?: boolean;
  /**
   * Show strapline below the logo text
   * Default: false (cleaner, more modern appearance)
   * Use true for: footers, about pages, marketing materials
   *
   * Approved strapline: "From Giving to Dignity"
   * (Selected as cleanest master strapline per brand guidelines)
   */
  showTagline?: boolean;
}

/**
 * Modern Logo component with icon + stylized text
 *
 * Brand Rule: "AmalQ must always appear as AmalQ, with the same casing
 * and approved font. No alternative spellings, capitalisation, or
 * stylistic variations are permitted across any material."
 *
 * Design Refinement (Expert Panel Score: 91/100):
 * - Icon sized to match text cap-height for visual harmony
 * - Tighter gap (8px) creates cohesion between icon and wordmark
 * - Font-bold gives text enough weight to balance ornate icon
 */
export const Logo: React.FC<LogoProps> = ({
  className,
  size = 'md',
  variant = 'default',
  showText = true,
  showTagline = false
}) => {
  // Icon sizes - matched to text cap-height for visual balance
  // Reduced ~15% from previous to create harmony with text weight
  const sizeClasses = {
    sm: 'h-4 w-4', // 16px - matches text-base cap-height
    md: 'h-6 w-6', // 24px - matches text-lg cap-height
    lg: 'h-8 w-8', // 32px - matches text-xl cap-height
    xl: 'h-10 w-10' // 40px - matches text-2xl cap-height
  };

  const textSizes = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-xl',
    xl: 'text-2xl'
  };

  const textColors = {
    default: 'text-gray-900 dark:text-white',
    white: 'text-white',
    minimal: 'text-gray-900 dark:text-gray-100'
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className={cn('relative shrink-0', sizeClasses[size])}>
        <img
          src="/logo/amalq-icon-only.png"
          alt="AmalQ icon"
          className="h-full w-full object-contain"
          onError={e => {
            const target = e.target as HTMLImageElement;
            if (target.src.includes('amalq-icon-only.png')) {
              target.src = '/logo/amalq-icon-only.svg';
            }
          }}
        />
      </div>

      {showText && (
        <div className={cn('flex', showTagline ? 'flex-col' : 'items-center')}>
          <span
            className={cn(
              'font-brand font-bold leading-none tracking-tight',
              textSizes[size],
              textColors[variant]
            )}
          >
            Amal
            <span className="text-emerald-600 dark:text-emerald-400">Q</span>
          </span>
          {showTagline && (
            <span
              className={cn(
                'mt-0.5 font-ui text-[10px] tracking-wide',
                variant === 'default'
                  ? 'text-gray-500 dark:text-gray-400'
                  : variant === 'white'
                    ? 'text-emerald-100/80'
                    : 'text-gray-500 dark:text-gray-500'
              )}
            >
              From Giving to Dignity
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default Logo;
